import React, { useContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useSidebarOpen from 'pages/Hooks/useSidebarOpen';
import { Portal } from '@chakra-ui/portal';
import Navbar from 'components/navbar';
import Sidebar from 'components/sidebar';
import Footer from 'components/footer/Footer';
import routes from 'routes';
import { AuthContext } from 'contexts/AuthContext';

export default function HeadHRLayout(props) {
  const { ...rest } = props;
  const location = useLocation();
  const { state } = useContext(AuthContext);
  const [open, setOpen] = useSidebarOpen();
  const [hovered, setHovered] = React.useState(false);
  const [currentRoute, setCurrentRoute] = React.useState('Head HR Dashboard');

  // Get user info for permission checking
  const user = state?.user;
  const hrRole = user?.hrRole;
  const allPermission = user?.allPermission;
  const isJrHRWithoutAllPermission = hrRole === 'jrhr' && !allPermission;

  // Filter routes based on permissions
  const filterRoutes = (routes) => {
    return routes.map((route) => {
      // Check if this route should be hidden for JRHR without all permissions
      const shouldHide =
        isJrHRWithoutAllPermission && route.requiresAllPermission;

      if (shouldHide) {
        return { ...route, hidden: true };
      }

      // If route has nested items, filter them too
      if (route.items) {
        const filteredItems = route.items.map((item) => {
          if (item.items) {
            // Filter nested items (like Students section)
            return {
              ...item,
              items: item.items.map((nestedItem) => {
                const nestedShouldHide =
                  isJrHRWithoutAllPermission &&
                  nestedItem.requiresAllPermission;
                return nestedShouldHide
                  ? { ...nestedItem, hidden: true }
                  : nestedItem;
              }),
            };
          }
          const itemShouldHide =
            isJrHRWithoutAllPermission && item.requiresAllPermission;
          return itemShouldHide ? { ...item, hidden: true } : item;
        });

        // If all items are hidden, hide the parent route too
        const allItemsHidden = filteredItems.every((item) => item.hidden);
        return {
          ...route,
          items: filteredItems,
          hidden: allItemsHidden,
        };
      }

      return route;
    });
  };

  const filteredRoutes = filterRoutes(routes);


  React.useEffect(() => {
    getActiveRoute(filteredRoutes);
  }, [location.pathname, filteredRoutes]);

  const getActiveRoute = (routes) => {
    let activeRoute = 'Head HR Dashboard';
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          setCurrentRoute(routes[i].name);
        }
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };

  const getRoutes = (routes) => {
    return routes
      .filter((prop) => !prop.hidden)
      .map((prop, key) => {
        if (prop.layout === '/headhr') {
          return (
            <Route path={`${prop.path}`} element={prop.component} key={key} />
          );
        }
        if (prop.collapse) {
          return getRoutes(prop.items);
        }
        return null;
      });
  };

  document.documentElement.dir = 'ltr';

  return (
    <div className="flex h-full w-full bg-background-100 dark:bg-background-900">
      <Sidebar
        open={open}
        hovered={hovered}
        setHovered={setHovered}
        mini={props.mini}
        onClose={() => setOpen(false)}
        routes={filteredRoutes}
      />
      <div className="mt-[60px] mt-[60px] h-full w-full font-dm dark:bg-background-900 md:mt-10">
        <main
          className={`mx-2.5 flex-none transition-all dark:bg-background-900 md:pr-2 ${
            props.mini === false
              ? 'lg:ml-[313px]'
              : props.mini === true && hovered === true
              ? 'lg:ml-[313px]'
              : 'ml-0 lg:ml-[142px]'
          } `}
        >
          <div>
            <Portal>
              <Navbar
                onOpenSidenav={() => setOpen(!open)}
                brandText={currentRoute}
                secondary={getActiveNavbar(filteredRoutes)}
                theme={props.theme}
                setTheme={props.setTheme}
                hovered={hovered}
                mini={props.mini}
                setMini={props.setMini}
                {...rest}
              />
            </Portal>
            <div className="mx-auto min-h-screen p-2 !pt-[calc(var(--navbar-bottom,148px)_-_48px)] md:p-2 md:!pt-[100px]">
              <Routes>
                {getRoutes(filteredRoutes)}
                <Route
                  path="/"
                  element={<Navigate to="/headhr/dashboard" replace />}
                />
              </Routes>
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
